/**
 * 厂商与密钥页（S1）的文案。命名空间 `visualGameAdmin.llm.providers.*`。
 *
 * **这里没有说明性文案。** 规则说不清的东西不写成一段话，写成行为：不可改的字段 `disabled`，
 * 不能停的按钮 `disabled` + 一句 hover 提示，继承来的值放 placeholder。背景知识在
 * `plan/llm-admin/`，不在页面上。
 *
 * `en` 与 `zh` 的 key 集必须逐字相同 —— `test/i18n.test.js` 拍平后逐项比对六个语种
 * （`ja`/`es`/`id`/`pt` 是 `structuredClone(en)` 的桩，自动跟随）。
 */

export const en = {
  title: 'Providers',
  addProvider: 'Add provider',
  editProvider: 'Edit provider',
  filters: {
    enabled: 'Status',
    all: 'All',
    enabledOnly: 'Enabled',
    disabledOnly: 'Disabled',
  },
  table: {
    name: 'Provider',
    capabilities: 'Capabilities',
    adapter: 'Adapter',
    baseUrl: 'Base URL',
    enabled: 'Status',
    keys: 'Keys',
    models: 'Models',
    actions: 'Actions',
    open: 'Manage',
    countHint: 'enabled / total',
  },
  credentials: {
    key_pool: 'API key pool',
    codex_auth: 'Codex login',
  },
  status: {
    enabled: 'Enabled',
    disabled: 'Disabled',
  },
  empty: {
    title: 'No provider yet',
  },
  detail: {
    back: 'Back',
  },
  basic: {
    title: 'Basic information',
    name: 'Name',
    nameLocked: 'Identity, not editable',
    adapter: 'Adapter',
    baseUrl: 'Base URL',
    credentialSource: 'Credentials',
    params: 'Default params (JSON)',
    enabled: 'Status',
    save: 'Save',
    delete: 'Delete provider',
    deleteConfirm: 'Delete provider, keys and models?',
  },
  providerForm: {
    createTitle: 'Add provider',
    editTitle: 'Edit provider',
    code: 'Provider code',
    displayName: 'Display name',
  },
  capabilityForm: {
    createTitle: 'Add capability',
    editTitle: 'Edit capability',
    code: 'Connection code',
    capability: 'Model capability',
  },
  capabilities: {
    title: 'Capability connections',
    add: 'Add capability',
    capability: 'Capability',
    code: 'Connection code',
    models: 'Models',
    edit: 'Edit',
  },
  serviceKeys: {
    title: 'Service credentials',
    hint: 'Credentials for interactive-novel avatars: Vidu (API Token, optional host) and Alibaba Cloud ARTC (AppKey and AppId). Until both have an enabled key, players do not see the avatar switch.',
  },
  keys: {
    title: 'Key pool',
    add: 'Add key',
    runtime: 'Key runtime',
    empty: 'No key yet',
    lastEnabled: 'Keep one enabled',
    table: {
      key: 'Key',
      label: 'Label',
      weight: 'Weight',
      concurrency: 'Concurrency',
      tpm: 'TPM limit',
      rpm: 'RPM limit',
      priority: 'Priority',
      enabled: 'Status',
      actions: 'Actions',
    },
    unlimited: 'Unlimited',
    actions: {
      edit: 'Edit',
      enable: 'Enable',
      disable: 'Disable',
      delete: 'Delete',
    },
    confirm: {
      disable: 'Disable this key?',
      delete: 'Delete this key?',
    },
  },
  form: {
    createTitle: 'Add a key',
    editTitle: 'Edit key',
    apiKey: 'Secret',
    keepSecret: 'Unchanged if empty',
    accountId: 'Account ID',
    accountIdHint: 'Composite credentials only, e.g. Doubao TTS appId; leave empty otherwise',
    vendorFields: {
      avatar: { apiKey: 'Vidu API Token', baseUrl: 'Vidu host', baseUrlHint: 'Optional: an https origin such as https://api.vidu.com; empty uses https://api.vidu.cn.' },
      rtc: { apiKey: 'ARTC AppKey', accountId: 'ARTC AppId', accountIdHint: 'Required: the AppId of the Alibaba Cloud ARTC application' },
    },
    label: 'Label',
    baseUrl: 'Base URL',
    weight: 'Weight',
    concurrency: 'Max concurrency',
    tpm: 'Max TPM',
    rpm: 'Max RPM',
    priority: 'Priority',
    enabled: 'Status',
    submit: 'Save key',
  },
  errors: {
    required: 'Required.',
    positiveInt: 'Must be a whole number greater than 0.',
    integer: 'Must be a whole number.',
    tooLong: 'Too long.',
    httpsOrigin: 'Must be an https origin such as https://api.vidu.com.',
    invalidJson: 'Not valid JSON.',
    notObject: 'Must be a JSON object.',
    unknownAdapter: 'Not a registered adapter.',
  },
  toast: {
    saved: 'Saved.',
    keyDeleted: 'Key deleted.',
    providerDeleted: 'Provider deleted.',
    failed: 'The change was refused.',
  },
}

export const zh = {
  title: '厂商',
  addProvider: '新增厂商',
  editProvider: '编辑厂商',
  filters: {
    enabled: '状态',
    all: '全部',
    enabledOnly: '已启用',
    disabledOnly: '已停用',
  },
  table: {
    name: '厂商',
    capabilities: '模型能力',
    adapter: '适配器',
    baseUrl: 'Base URL',
    enabled: '状态',
    keys: 'API Key',
    models: '模型',
    actions: '操作',
    open: '管理',
    countHint: '启用 / 总数',
  },
  status: {
    enabled: '已启用',
    disabled: '已停用',
  },
  empty: {
    title: '还没有接入厂商',
  },
  detail: {
    back: '返回列表',
  },
  basic: {
    title: '基本信息',
    name: '名称',
    nameLocked: '身份，不可改',
    adapter: '适配器',
    baseUrl: 'Base URL',
    credentialSource: '凭据来源',
    params: '默认参数（JSON）',
    enabled: '状态',
    save: '保存',
    delete: '删除厂商',
    deleteConfirm: '同时删除相关 API Key 和模型？',
  },
  providerForm: {
    createTitle: '新增厂商',
    editTitle: '编辑厂商',
    code: '厂商代码',
    displayName: '显示名称',
  },
  capabilityForm: {
    createTitle: '新增能力接入',
    editTitle: '编辑能力接入',
    code: '接入代码',
    capability: '模型能力',
  },
  capabilities: {
    title: '能力接入',
    add: '新增能力接入',
    capability: '模型能力',
    code: '接入代码',
    models: '管理模型',
    edit: '编辑',
  },
  credentials: {
    key_pool: 'API Key',
    codex_auth: 'Codex 登录凭据',
  },
  serviceKeys: {
    title: '服务凭据',
    hint: '互动小说数字人用的凭据：Vidu（API Token，可选主机）与阿里云 ARTC（AppKey 与 AppId）。两类都有启用的凭据之前，玩家端不显示数字人开关。',
  },
  keys: {
    title: 'API Key',
    add: '添加 API Key',
    runtime: '查看 API Key 状态',
    empty: '还没有 API Key',
    lastEnabled: '至少保留一个已启用的 API Key',
    table: {
      key: 'API Key',
      label: '标签',
      weight: '权重',
      concurrency: '并发上限',
      tpm: 'TPM 上限',
      rpm: 'RPM 上限',
      priority: '优先级',
      enabled: '状态',
      actions: '操作',
    },
    unlimited: '不限',
    actions: {
      edit: '编辑',
      enable: '启用',
      disable: '停用',
      delete: '删除',
    },
    confirm: {
      disable: '停用这个 API Key？',
      delete: '删除这个 API Key？',
    },
  },
  form: {
    createTitle: '添加 API Key',
    editTitle: '编辑 API Key',
    apiKey: 'API Key',
    keepSecret: '不填则不换',
    accountId: '账号 ID',
    accountIdHint: '豆包 TTS 需要填写 App ID；其他服务通常无需填写',
    vendorFields: {
      avatar: { apiKey: 'Vidu API Token', baseUrl: 'Vidu 主机', baseUrlHint: '可选：https 源，如 https://api.vidu.com；留空用 https://api.vidu.cn。' },
      rtc: { apiKey: 'ARTC AppKey', accountId: 'ARTC AppId', accountIdHint: '必填：阿里云 ARTC 应用的 AppId' },
    },
    label: '标签',
    baseUrl: 'Base URL',
    weight: '权重',
    concurrency: '并发上限',
    tpm: 'TPM 上限',
    rpm: 'RPM 上限',
    priority: '优先级',
    enabled: '状态',
    submit: '保存 API Key',
  },
  errors: {
    required: '必填。',
    positiveInt: '必须是大于 0 的整数。',
    integer: '必须是整数。',
    tooLong: '太长了。',
    httpsOrigin: '必须是 https 源，例如 https://api.vidu.com。',
    invalidJson: '不是合法的 JSON。',
    notObject: '必须是一个 JSON 对象。',
    unknownAdapter: '不是已注册的适配器。',
  },
  toast: {
    saved: '已保存。',
    keyDeleted: 'API Key 已删除。',
    providerDeleted: '厂商已删除。',
    failed: '这次改动被拒绝了。',
  },
}
